import React from "react";
import { trackEvent } from "@leafly-com/web-utils";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";

import getStrain from "api/requests/getStrain";
import getStrainReviews from "api/requests/getStrainReviews";
import { ITEMS_PER_STRAIN_REVIEW_PAGE } from "constants/strains";
import { StrainPageProvider } from "context/StrainPageContext";
import { StrainReview } from "custom-types/Reviews";
import { StrainV2 } from "custom-types/StrainV2";
import getStrainRedirect from "utils/getStrainRedirect";

import StarRating from "components/botanic/StarRating";
import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import GoogleAd from "components/GoogleAd";
import MetaTags from "components/MetaTags";
import PageNavigationV2 from "components/PageNavigationV2";
import Sort from "components/Sort";
import Divider from "components/StrainPage/Divider";
import StrainDispensariesSection from "components/StrainPage/StrainDispensariesSection";
import StrainPhotos from "components/StrainPage/StrainPhotos";
import StrainSensations from "components/StrainPage/StrainSensations";
import WriteReviewButton from "components/StrainPage/WriteReviewButton";
import StrainReviewCardContainer from "components/StrainReviewCardContainer";
import TrackImpression from "components/TrackImpression";

type Props = {
  strain: StrainV2;
  reviews: StrainReview[];
  totalCount: number;
};

const ReviewsPage: NextPage<Props> = ({ strain, reviews, totalCount }) => {
  const router = useRouter();
  const {
    query: { page },
  } = router;

  const {
    name,
    highlightedPhotos = [],
    rating,
    phenotype,
    slug,
    averageRating,
    reviewCount,
  } = strain || {};

  const currentPage = page ? Number(page) : 1;

  const firstReviewSet = reviews.slice(0, 3);
  const secondReviewSet = reviews.slice(3, 8);

  const shortDescription = `Read people’s experiences with the cannabis strain ${name}.`;

  return (
    <>
      <StrainPageProvider strain={strain} eventPage="Strain Review page">
        <MetaTags
          title={`${strain.name} Weed Strain Effects & Reviews | Leafly`}
          description={`Discover ${strain.name} weed and read reviews of the effects and feelings cannabis consumers report from this marijuana strain.`}
          canonicalParams={[]}
        />
        <section className="container mt-xxl">
          <div className="md:flex justify-between mb-xxl">
            <div className="col md:col-1/2 mb-lg md:mb-none">
              <h1 className="mb-xs">{name} reviews</h1>
              <StarRating
                className="text-sm mb-sm"
                starWidth={16}
                rating={Math.round(averageRating * 10) / 10}
                ratingCount={reviewCount}
              />
              <p className="text-xs mb-none">{shortDescription}</p>
            </div>
            <div className="col md:col-1/2">
              <TrackImpression
                category="Reviews"
                label="strain write a review CTA"
                className="w-full flex justify-end items-start"
              >
                <WriteReviewButton
                  slug={slug}
                  onClick={() => {
                    trackEvent("Reviews", "Click", "strain write a review CTA");
                  }}
                />
              </TrackImpression>
            </div>
          </div>

          <div className="gam-rectangular-fixed-height">
            <GoogleAd
              adPath="/13212770/strain/reviews_upper"
              sizes={{ desktop: [728, 90], mobile: [320, 50] }}
              targeting={{ strain: strain.slug }}
            />
          </div>
          {currentPage === 1 && (
            <>
              <StrainSensations className="my-xxl" />
              <Divider />
            </>
          )}

          <div
            itemProp={totalCount > 0 ? "itemReviewed" : ""}
            itemType={totalCount > 0 ? "https://schema.org/Product" : ""}
            itemScope
            className="mt-xxl"
          >
            <meta itemProp="name" content={name} />
            <meta itemProp="description" content={shortDescription} />
            <div
              itemProp="aggregateRating"
              itemType="http://schema.org/AggregateRating"
              itemScope
            >
              <meta itemProp="reviewCount" content={`${totalCount}`} />
              <meta itemProp="ratingValue" content={`${rating}`} />
            </div>
            {currentPage === 1 && (
              <div className="md:flex justify-between mb-sm">
                <div className="col md:col-1/2">
                  <h2 className="mb-xs">{name} reviews</h2>
                  <StarRating
                    className="text-sm mb-sm"
                    starWidth={16}
                    rating={Math.round(averageRating * 10) / 10}
                    ratingCount={reviewCount}
                  />
                </div>
                <div className="col md:col-1/2 flex justify-end items-start my-lg md:my-none">
                  <TrackImpression
                    category="Reviews"
                    label="strain write a review CTA"
                    className="w-full flex justify-end items-start"
                  >
                    <WriteReviewButton
                      slug={slug}
                      onClick={() => {
                        trackEvent(
                          "Reviews",
                          "Click",
                          "strain write a review CTA",
                        );
                      }}
                    />
                  </TrackImpression>
                </div>
              </div>
            )}

            <Sort
              sortOptions={{
                "Least Helpful": "upvotes_count:asc",
                "Most Helpful": "upvotes_count:desc",
                Newest: "created:desc",
                Oldest: "created:asc",
              }}
              onClick={(option) =>
                trackEvent("Strain Reviews Sort", "Click", option)
              }
              defaultOption={1}
              shallowRouting={false}
            />

            {firstReviewSet.map((review: StrainReview) => (
              <StrainReviewCardContainer
                review={review}
                strain={strain}
                key={`review-card-${review.id}`}
              />
            ))}

            <GoogleAd
              adPath="/13212770/strain/reviews_lower"
              className="gam-square-fixed-height my-xxl"
              sizes={{ desktop: [300, 250], mobile: [300, 250] }}
              targeting={{
                section: "strain",
                strain: slug,
                zone: "abvf",
              }}
            />

            {secondReviewSet.map((review: StrainReview) => (
              <StrainReviewCardContainer
                review={review}
                strain={strain}
                key={review.id}
              />
            ))}

            <PageNavigationV2
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / ITEMS_PER_STRAIN_REVIEW_PAGE)}
              className="mt-4 mb-6"
            />
          </div>

          {currentPage === 1 && highlightedPhotos?.length > 0 && (
            <StrainPhotos className="my-xl" />
          )}

          <div className="full-bleed-container">
            <StrainDispensariesSection />
          </div>
        </section>
        <Breadcrumbs>
          <Breadcrumb href="/strains">Weed Strains</Breadcrumb>
          {!!phenotype && (
            <Breadcrumb
              href={`/strains/lists/category/${phenotype.toLowerCase()}`}
            >
              {phenotype}
            </Breadcrumb>
          )}
          <Breadcrumb href={`/strains/${slug}`}>{name}</Breadcrumb>
          <Breadcrumb href={`/strains/${slug}/reviews`} currentLocation={true}>
            {`${name} Reviews`}
          </Breadcrumb>
        </Breadcrumbs>
      </StrainPageProvider>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context;
  const slug = Array.isArray(query?.strainSlug)
    ? query.strainSlug[0]
    : query.strainSlug || "";

  const redirect = getStrainRedirect(slug, `/strains/${slug}/reviews`);

  if (redirect) {
    return {
      redirect,
    };
  }

  const [
    strain,
    {
      data: reviews,
      metadata: { totalCount },
    },
  ] = await Promise.all([
    getStrain(slug),
    getStrainReviews(
      slug,
      ITEMS_PER_STRAIN_REVIEW_PAGE,
      parseInt(query?.page as string),
      query.sort,
    ),
  ]);

  // We want the page to redirect to 404 when no reviews are present or review page does not exist.
  const pageDoesNotExist =
    totalCount / ITEMS_PER_STRAIN_REVIEW_PAGE < parseInt(query?.page as string);

  if (!strain || pageDoesNotExist) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      reviews,
      strain,
      totalCount,
    },
  };
};

export default ReviewsPage;
